import {
	Address,
	GoogleStorageDocument,
} from '../../../../data/models/companyExternalUser';

export enum OnboardingStatus {
	WELCOME = 'welcome',
	VERIFYING = 'verifying',
	PASSWORD_ENTRY = 'password_entry',
	FORM = 'form',
	SUBMITTING = 'submitting',
	SUCCESS = 'success',
	ERROR = 'error',
}

export enum OnboardingStep {
	BASIC_INFO = 0,
	PROFESSIONAL_DETAILS = 1,
	WORK_LOGISTICS = 2,
	VEHICLE_TRAVEL = 3,
	TRAVEL_PREFERENCES = 4,
	REPRESENTATION = 5,
	REVIEW = 6,
}

export type FormValues = {
	// Overview - Basic fields
	role?: string;
	groups?: string[];

	// Basic Information - Who are you
	firstName: string;
	lastName: string;
	preferredName: string;
	preferredPronouns: string;
	dateOfBirth: string;
	profilePictureUrl?: GoogleStorageDocument;

	// Basic Information - Contact Information
	email: string;
	phone: string;
	phoneCarrier: string;
	isIphone: string; // 'yes' | 'no'
	smsTextReady: string; // 'yes' | 'no'
	hasWhatsapp: string; // 'yes' | 'no'
	whatsappNumber: string;
	hasWechat: string; // 'yes' | 'no'
	wechatNumber: string;

	// Basic Information - Emergency Contact
	emergencyContactFirstName: string;
	emergencyContactLastName: string;
	emergencyContactPhone: string;

	// Basic Information - Home Address
	homeAddress: Address;

	// Basic Information - Shipping Address
	shippingIsSameAsHome: boolean;
	shippingAddress: Address;

	// Professional Details - Job Status
	currentJobStatus: string;
	wantFreelanceContact: string;
	interestedInW2Positions: string;
	interestedInInternship: string;

	// Professional Details - Union Status
	unionStatus: string;
	unionName: string;

	// Professional Details - Primary Production Role
	productionRole: string;
	yearsOfExperiencePrimary: string;
	rateTenOrTwelveHours: string;
	isRateFixed: boolean | undefined;
	chargesOvertimeTypically: boolean | undefined;
	workReferenceFirstName: string;
	workReferenceLastName: string;
	workReferenceEmail: string;
	workReferencePhone: string;
	workReference2FirstName: string;
	workReference2LastName: string;
	workReference2Email: string;
	workReference2Phone: string;

	// Professional Details - Secondary Production Role
	secondaryProductionRole: string;
	secondaryRate: string;
	secondaryRateType: string;
	secondaryYearsOfExperience: string;
	secondaryRateTenOrTwelveHours: string;
	secondaryIsRateFixed: boolean | undefined;
	secondaryChargesOvertimeTypically: boolean | undefined;
	secondaryWorkReferenceFirstName: string;
	secondaryWorkReferenceLastName: string;
	secondaryWorkReferenceEmail: string;
	secondaryWorkReferencePhone: string;
	secondaryWorkReference2FirstName: string;
	secondaryWorkReference2LastName: string;
	secondaryWorkReference2Email: string;
	secondaryWorkReference2Phone: string;

	// Professional Details - Work Samples and Social
	websiteUrl: string;
	instagramHandle: string;
	resumeProjectHistoryUploads: GoogleStorageDocument[];

	// Professional Details - Kits and Equipment
	hasKit: boolean;
	kitContents: string;
	kitFee: string;
	kitDocuments: GoogleStorageDocument[];

	// Professional Details - Preferences
	willingToBePhotographedOnSet: boolean;
	dietaryRestrictions: string;
	shirtSize: string;
	shoeSize: string;

	// Professional Details - Certifications
	certifications: string[];

	// Work Logistics - Local Work Location
	localWorkLocations: string[];

	// Work Logistics - Employment Type
	employmentTypePreference: string;
	loanOutCompanyName: string;
	loanOutCompanyContactName: string;
	loanOutCompanyPhone: string;
	loanOutCompanyEmail: string;

	// Work Logistics - Payment
	preferredPaymentMethods: string[];

	// Vehicle & Travel - Travel Basics
	globalEntry: string;
	passportIssuingCountry: string;
	passportExpirationDate: string;
	noPassport: boolean | undefined;
	willingToTravelDomestically: string; // 'yes' | 'no'
	willingToTravelInternationally: string; // 'yes' | 'no'

	// Representation - Agent/Management
	hasRepresentation: boolean | undefined;
	representativeFullName: string;
	representativeCompany: string;
	representativePhone: string;
	representativeEmail: string;
	billThroughAgentCompany: string; // 'yes' | 'no'

	// Vehicle & Travel - Vehicle Information
	driversLicenseState: string;
	noDriversLicense: boolean | undefined;
	hasVehicle: boolean | undefined;
	willingToUseVehicleForProduction: boolean | undefined;
	vehicleType: string; // 'sedan' | 'suv' | 'truck' | 'van'
	vehicleLocation: string;

	// Travel Preferences - Airline
	preferredHotels: string[];
	preferredCarRentals: string[];
	preferredRideSharingApps: string[];
	unitedMileage: string;
	hiltonNumber: string;
	marriottBonvoyNumber: string;
	avisNumber: string;
	budgetNumber: string;

	// Legacy fields (keeping for backward compatibility)
	driversLicenseNumber: string;
	streetAddress1: string;
	streetAddress2: string;
	city: string;
	state: string;
	zip: string;
	rate: string;
	rateType: string;
	homeAirport: string;
	passportNumber: string;
	tsaPrecheck: string;
	amtrackNumber: string;
	deltaMileage: string;
	preferredSeating: string;
	starwoodMember: string;
	emeraldClub: string;
	preferredAirlines: string[];
};
